import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { motion } from 'framer-motion'
import Box from '@aura/components/box'
import Button from '@aura/components/button'
import Grid from '@aura/components/grid'
import Typography from '@aura/components/typography'
import { withStyles } from '@aura/components/styles'
import LanguageIcon from '@mui/icons-material/Language'
import language from '../../constants/languages'
import { isFrench } from '../../constants/app'
import styles from './styles'


const HedgeXLanding = ({ classes }) => {
    const { t: translate, i18n } = useTranslation()
    const history = useHistory()

    const toggleLanguage = useCallback(() => {
        const newLang = isFrench(i18n.language) ? language.ENGLISH : language.FRENCH
        i18n.changeLanguage(newLang)
    }, [ i18n ])

    const handleLogin = useCallback(() => {
        history.push('/login')
    }, [ history ])

    return (
        <Box className={classes.root}>
            {/* Background decorative elements */}
            <Box className={classes.backgroundOverlay} />            
            {/* Header */}
            <Grid container className={classes.header} justifyContent="space-between" alignItems="center">
                {/* RBC Logo */}
                <Grid item className={classes.logoContainer}>
                    <img 
                        src={isFrench(i18n.language) 
                            ? '/img/logos/rbc/RBC_Capital_Markets_logo_fr.svg' 
                            : '/img/logos/rbc/RBC_Capital_Markets_logo_en.svg'} 
                        alt="RBC Capital Markets Logo" 
                        className={classes.rbcLogo}
                    />
                </Grid>
                
                {/* Language Switcher */}
                <Grid item>
                    <Button 
                        variant="text" 
                        className={classes.languageButton}
                        onClick={toggleLanguage}
                        disableRipple
                    >
                        <LanguageIcon className={classes.languageIcon} />
                        {isFrench(i18n.language) ? 'English' : 'Français'}
                    </Button>
                </Grid>
            </Grid>

            {/* Main Content */}
            <Grid container className={classes.mainContent} spacing={0} alignItems="flex-start">
                {/* Left Section - Welcome */}
                <Grid item className={classes.welcomeContainer}>
                    <Typography className={classes.welcomeText}>
                        {translate('hedgex.welcome')}
                    </Typography>
                        
                    {/* HedgeX Logo */}
                    <Box className={classes.hedgexLogoContainer}>
                        <img  src="\img\hedgex\Hedgex_logo.svg" alt="HedgeX Logo" />
                    </Box>

                    {/* Login Button */}
                    <Button 
                        variant="contained" 
                        className={classes.loginButton}
                        onClick={handleLogin}
                        fullWidth
                    >
                        {translate('hedgex.login')}
                    </Button>

                    {/* Additional Links */}
                    <Box className={classes.linksContainer}>
                        <Typography className={classes.poweredByText}>
                            {translate('hedgex.poweredBy')}
                        </Typography>
                        <Typography className={classes.digitalMarketplaceText}>
                            {translate('hedgex.digitalMarketplace')}
                        </Typography>
                    </Box>

                    {/* Horizontal Divider */}
                    <Box className={classes.dividerLine} />

                    {/* Assistance Section */}
                    <Box className={classes.assistanceContainer}>
                        <Box className={classes.assistanceTitleWrapper}>
                            <Typography className={classes.assistanceTitle}>
                                {translate('hedgex.needAssistance')}
                            </Typography>
                        </Box>
                        <Box className={classes.assistanceContactWrapper}>
                            <Typography className={classes.assistanceBody}>
                                <span className={classes.contactLabel}>{translate('hedgex.emailLabel') || 'Email:'}</span>{' '}
                                <a href="mailto:rbchedgex@rbccm.com" className={classes.contactValue}>rbchedgex@rbccm.com</a>
                            </Typography>
                            <Typography className={classes.assistanceBody}>
                                <span className={classes.contactLabel}>{translate('hedgex.telephoneLabel') || 'Telephone:'}</span>{' '}
                                <a href="tel:+12128586001" className={classes.contactValue}>+ 1 212 858 6001</a>
                            </Typography>
                        </Box>
                    </Box>

                </Grid>
                {/* First Glass Card - Text Content */}
                <Grid item className={classes.firstCardWrapper}>
                    <Box className={classes.glassCard}>
                        <Typography variant="h4" className={classes.cardTitle}>
                            {translate('hedgex.cardTitle')}
                        </Typography>
                        
                        <Typography variant="h7" className={classes.cardDescription}>
                            {translate('hedgex.cardDescription')}
                        </Typography>
                    </Box>
                </Grid>
                {/* Second Glass Card - Preview Section */}
                <Grid item className={classes.secondCardWrapper}>
                    <Box className={classes.secondCardGlassCard}>
                        <Box className={classes.previewContainer}>
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.6, duration: 0.8, ease: 'easeOut' }}
                                style={{ position: 'relative', transform: 'translateY(0)' }}
                            >
                                <Box
                                    component='img'
                                    src='/img/hedgex/landing_page_laptop.svg'
                                    alt='Laptop Preview'
                                    className={classes.previewDesktop}
                                />
                            </motion.div>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Footer */}
            <Grid container className={classes.footer} justifyContent="flex-start" alignItems="center">
                <Grid item>
                    <Typography className={classes.footerCopyright}>
                        {translate('hedgex.copyright') }
                    </Typography>
                </Grid>
            </Grid>

        </Box>
    )
}

HedgeXLanding.propTypes = {
    classes: PropTypes.object.isRequired
}

export default withStyles(HedgeXLanding, styles)
